import './QuizPlayer.less';
import React, {useRef, useState} from 'react';
import QuestionCard from "./question-card/QuestionCard";
import Button from "../../../bootstrap/components/button/Button";
import __ from "../../i18n";
import {compareArrays, compareUnsortedArrays} from "../../../bootstrap/utils/comparative";
import {
    TYPE_CHECKBOXES,
    TYPE_RADIO_BUTTONS,
    TYPE_TEXT_COMPLETION,
    TYPE_WORD_ARRANGEMENT
} from "../../model-constants/quiz/types";
import DraftView from "../../components/draft-view/DraftView";
import {jsonToObject} from '../../../bootstrap/utils/json';
import useMediaQuery from '../../hooks/useMediaQuery';
import screenSizeQueries from '../../styles/screenSizeQueries';


export default function QuizPlayer({
    questionGroups: sections,
    questions: _questions,
    onClose = null,
}) {
    const isMobile = useMediaQuery(screenSizeQueries.mobile);

    const ref = useRef(null);

    const questions = _questions.map(q => {
        return {
            ...q,
            answer_value: q.type === TYPE_RADIO_BUTTONS
                ? JSON.parse(q.answer_values)[0][0]
                : JSON.parse(q.answer_values)[0],
            pieces: JSON.parse(q.pieces).map((p, i) => {
                p.id = i;
                return p;
            })
        };
    });

    const getDefaultValue = (questionType) => {
        switch (questionType) {
            case TYPE_RADIO_BUTTONS:
                return null;
            case TYPE_CHECKBOXES:
                return [];
            case TYPE_TEXT_COMPLETION:
                return [];
            case TYPE_WORD_ARRANGEMENT:
                return [];
        }
    };

    const compareValues = (questionType, value1, value2) => {
        switch (questionType) {
            case TYPE_RADIO_BUTTONS:
                return value1 === value2;
            case TYPE_CHECKBOXES:
                return compareUnsortedArrays(value1, value2);
            case TYPE_TEXT_COMPLETION:
                return compareArrays(value1, value2);
            case TYPE_WORD_ARRANGEMENT:
                return compareArrays(value1.map(t => t.value), value2);
        }
    };

    const [values, setValues] = useState((() => {
        const defaultValues = {};
        questions.map(question => {
            defaultValues[question.id] = getDefaultValue(question.type);
        });
        return defaultValues;
    })());

    const setValue = (questionId, value) => {
        setValues(values => ({
            ...values,
            [questionId]: value
        }));
    };

    const isQuestionAnsweredCorrectly = (question) => {
        const value = values[question.id];
        return compareValues(question.type, value, question.answer_value);
    };

    const getQuestionScore = (question) => {
        if (isQuestionAnsweredCorrectly(question)) {
            return question.score;
        }
        return 0;
    };

    const getTotalScore = () => {
        return questions.reduce((score, question) => score + getQuestionScore(question), 0);
    };

    const getMaxScore = () => {
        return questions.reduce((score, question) => score + question.score, 0);
    };

    const isFulfilled = questions.every(question => {
        return !compareValues(question.type, getDefaultValue(question.type), values[question.id]);
    });

    const [ended, setEnded] = useState(false);

    const items = [
        ...sections,
        ...questions.filter(q => q.group_id === null),
    ];
    items.sort((a, b) => a.sort_order - b.sort_order);

    questions.sort((a, b) => a.sort_order - b.sort_order);

    let questionPosition = 0;

    return <div className="quiz-player" ref={ref}>
        <div className="content">
            {
                items.map((item) => {
                    if (item.hasOwnProperty('group_id')) {
                        questionPosition++;

                        return <QuestionCard
                            key={'question/' + item.id}
                            anchorRef={ref}
                            question={item}
                            options={item.pieces}
                            value={values[item.id]}
                            onChange={value => setValue(item.id, value)}
                            quizEnded={ended}
                            questionPosition={questionPosition}
                            isCorrect={isQuestionAnsweredCorrectly(item)}
                        />;
                    }

                    const sectionDescription = jsonToObject(item.description);
                    const sectionQuestions = questions.filter(question => question.group_id === item.id);

                    return <div key={'questionGroup/' + item.id} className="section">
                        <div className="info">
                            {/* <h2 className="title">
                                {
                                    __('Questions ::start–::end', {
                                        start: questionPosition + 1,
                                        end: questionPosition + sectionQuestions.length
                                    })
                                }
                            </h2> */}
                            {
                                sectionDescription !== null &&
                                <div className="description">
                                    <DraftView contentState={sectionDescription} />
                                </div>
                            }
                        </div>
                        <div className="questions">
                            {
                                sectionQuestions
                                    .map((question) => {
                                        questionPosition++;
                                        return <QuestionCard
                                            key={'question/' + question.id}
                                            anchorRef={ref}
                                            question={question}
                                            options={question.pieces}
                                            value={values[question.id]}
                                            onChange={value => setValue(question.id, value)}
                                            quizEnded={ended}
                                            questionPosition={questionPosition}
                                            isCorrect={isQuestionAnsweredCorrectly(question)}
                                        />;
                                    })
                            }
                        </div>
                    </div>;
                })
            }
        </div>
        <div className="footer">
            {
                ended &&
                <div className="results">
                    {__('Total score: ::score/::max', {score: getTotalScore(), max: getMaxScore()})}
                </div>
            }
            {
                (onClose !== null || !ended) && (
                    <div className="actions">
                        {
                            !ended && 
                            <Button
                                title={__('See results')}
                                size={isMobile ? 'small' : 'large'}
                                appearance="solid-primary"
                                onClick={() => {
                                    setEnded(true);
                                }}
                                disabled={!isFulfilled}
                                tooltip={!isFulfilled ? __('Please answer all questions to see results') : undefined}
                            />
                        }
                        {
                            onClose !== null &&
                            <Button
                                type="button"
                                size={isMobile ? 'small' : 'large'}
                                appearance="text-neutral"
                                title={__('Close')}
                                onClick={onClose}
                            />
                        }
                    </div>
                )
            }
        </div>
    </div>;
}
